/**
 * <GeneratedActivityResults> — les énoncés que Sam propose (organism).
 *
 * Les trois cartes de `MaterialResultsCards`, côte à côte quand la place le permet et empilées
 * sinon. Rien d'autre : chaque carte se débrouille de son état, et l'organisme ne fait que les
 * poser et rendre leurs gestes à l'appelant, une carte à la fois.
 *
 * La largeur lue est celle du CONTENEUR (`onLayout`), jamais de la fenêtre : ces cartes vivent
 * dans une feuille bornée à 500 px, et trois colonnes y donnaient un mot par ligne. Même
 * mécanisme que `<NextSessionHero>` — on ne réinvente pas une seconde façon de mesurer.
 *
 * Une liste qui OFFRE : la règle des deux boutons ne s'y applique pas (`CLAUDE.md`, « une étape
 * demande, une feuille offre »). Ce sont trois propositions, pas trois réponses à une question.
 *
 * Tant que Sam compose les trois, la rangée est UNE vue qui charge : un seul `<DelayedSkeleton>`
 * autour d'elle, donc un seul balayage et un seul seuil. Dès qu'une carte arrive, la rangée a du
 * contenu — elle ne peut plus être un indicateur de chargement pour un lecteur d'écran —, et les
 * cartes encore en route gardent leur forme, fixe, jusqu'à la leur.
 */
import React, { useState } from "react"
import { View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIGeneratedActivity } from "../../../ports/primary/ui/types"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { type IconName } from "../../atoms/icons/Icon"
import { GeneratedActivityCard } from "../../molecules/GeneratedActivityCard"
import { type Voice } from "../../utils/voice"

/** En dessous, une colonne ne tient plus un énoncé : trois mots par ligne, ce n'est plus lisible. */
const MIN_COLUMN_WIDTH = 260

export type GeneratedActivityResultsProps = {
    activities: UIGeneratedActivity[]
    /** L'option « Quiz de prérequis » est proposée sur chaque carte. */
    quizAvailable?: boolean
    onToggleQuiz?: (id: string | number, next: boolean) => void
    onCreate?: (id: string | number) => void
    /** Un second geste sous chaque énoncé prêt (`<GeneratedActivityCard extraAction>`). Absent (défaut) : rien. */
    extraAction?: { label: string; iconName: IconName; onPress: (id: string | number) => void }
    /** À qui les cartes parlent. Défaut `"parent"`. */
    voice?: Voice
    /** La version courte de chaque carte (`<GeneratedActivityCard compact>`, le fil du live). Défaut `false`. */
    compact?: boolean
    testID?: string
}

export function GeneratedActivityResults(props: GeneratedActivityResultsProps) {
    const {
        activities,
        quizAvailable = false,
        onToggleQuiz,
        onCreate,
        extraAction,
        voice = "parent",
        compact = false,
        testID,
    } = props

    const [width, setWidth] = useState(0)
    const onLayout = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)
    const columns = width >= MIN_COLUMN_WIDTH * activities.length && activities.length > 1

    const cards = activities.map((activity) => (
        <GeneratedActivityCard
            key={activity.id}
            activity={activity}
            quizAvailable={quizAvailable}
            onToggleQuiz={(next) => onToggleQuiz?.(activity.id, next)}
            onCreate={() => onCreate?.(activity.id)}
            extraAction={
                extraAction != null ? { ...extraAction, onPress: () => extraAction.onPress(activity.id) } : undefined
            }
            voice={voice}
            compact={compact}
        />
    ))

    const row = columns ? (
        <Row gap="4" align="stretch">
            {cards.map((card, index) => (
                <Stack key={activities[index]!.id} style={styles.column}>
                    {card}
                </Stack>
            ))}
        </Row>
    ) : (
        <Stack gap="4">{cards}</Stack>
    )
    const allPending = activities.length > 0 && activities.every((activity) => activity.status === "pending")

    return (
        <View onLayout={onLayout} testID={testID}>
            {allPending ? <DelayedSkeleton accessibilityLabel="Génération en cours">{row}</DelayedSkeleton> : row}
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    column: { flex: 1 },
}))
